import { Component, inject, signal, OnInit, OnDestroy, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { PopoverModule, Popover } from 'primeng/popover';
import { MenuModule } from 'primeng/menu';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import type { MenuItem } from 'primeng/api';
import { AuthService } from '../../core/services/auth.service';
import { NotificationService } from '../../core/services/notification.service';
import { permissionLabel } from '../../core/constants/permissions';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [
    CommonModule, RouterOutlet, RouterLink, RouterLinkActive,
    ButtonModule, DrawerModule, AvatarModule, BadgeModule, OverlayBadgeModule,
    PopoverModule, MenuModule, ToastModule, TooltipModule
  ],
  templateUrl: './shell.html',
  styleUrl: './shell.scss'
})
export class ShellComponent implements OnInit, OnDestroy {
  auth = inject(AuthService);
  router = inject(Router);
  private route = inject(ActivatedRoute);
  notifService = inject(NotificationService);
  private bp = inject(BreakpointObserver);
  private toast = inject(MessageService);

  isMobile   = signal(false);
  drawerOpen = signal(false);

  // `permission: null` = toujours visible (tableau de bord). Les autres entrées
  // n'apparaissent que si le compte connecté a le droit correspondant (super-admin :
  // toujours tout, cf. AuthService.hasPermission).
  private readonly allNavItems = [
    { label: 'Tableau de bord', icon: 'pi pi-home', route: '/dashboard', permission: null as string | null },
    { label: 'Biens immobiliers', icon: 'pi pi-building', route: '/properties', permission: 'PROPERTIES_READ' },
    { label: "Documents d'inscription", icon: 'pi pi-file', route: '/back-office/documents', permission: 'DOCUMENTS_READ' },
    { label: 'Plans & Tarifs', icon: 'pi pi-crown', route: '/plans', permission: 'PLANS_READ' },
    { label: 'Utilisateurs', icon: 'pi pi-users', route: '/users', permission: 'USERS_READ' },
    { label: 'Publicités', icon: 'pi pi-megaphone', route: '/publicite', permission: 'ADS_READ' },
  ];

  get navItems() {
    return this.allNavItems.filter(item => item.permission === null || this.auth.hasPermission(item.permission));
  }

  profileMenuItems: MenuItem[] = [
    {
      label: 'Se déconnecter',
      icon: 'pi pi-sign-out',
      command: () => this.logout()
    }
  ];

  constructor() {
    this.bp.observe([Breakpoints.Handset]).subscribe(r => this.isMobile.set(r.matches));

    // Notification toast à chaque nouvelle notification SSE
    effect(() => {
      const notifs = this.notifService.notifications();
      if (notifs.length === 0) return;
      const last = notifs[0];
      const meta = this.notifService.metaFor(last.type);
      this.toast.add({
        severity: 'info',
        summary: meta.label,
        detail: last.message,
        life: 5000
      });
    });
  }

  ngOnInit(): void {
    this.notifService.connect();

    this.route.queryParamMap.subscribe(params => {
      const denied = params.get('accessDenied');
      if (!denied) return;
      this.toast.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: `Vous n'avez pas les droits nécessaires pour accéder à « ${permissionLabel(denied)} ». Contactez un administrateur si vous pensez qu'il s'agit d'une erreur.`,
        life: 6000
      });
      // Retire le paramètre de l'URL pour éviter de réafficher le toast sur un refresh.
      this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
    });
  }

  ngOnDestroy(): void {
    this.notifService.disconnect();
  }

  get user() { return this.auth.currentUser(); }
  get userName() {
    const u = this.user;
    if (!u) return '';
    return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
  }

  get userInitials() {
    const u = this.user;
    if (!u) return '?';
    if (u.firstName && u.lastName) return (u.firstName[0] + u.lastName[0]).toUpperCase();
    if (u.firstName) return u.firstName[0].toUpperCase();
    return u.email?.[0]?.toUpperCase() ?? '?';
  }

  get notifications() { return this.notifService.notifications(); }
  get unreadCount() { return this.notifService.unreadCount(); }

  toggleDrawer() { this.drawerOpen.update(v => !v); }
  closeDrawerOnNav() { if (this.isMobile()) this.drawerOpen.set(false); }

  openNotifications(panel: Popover, event: Event) {
    panel.toggle(event);
    this.notifService.markAllRead();
  }

  logout() { this.auth.logout(); }
}
